import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { detectDeviceLang } from "@/i18n";

export const LANGUAGES = [
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
];

const STORAGE_KEY = "summit.lang";
const LanguageContext = createContext(null);

export function LanguageProvider({ children, initial = null }) {
  const { i18n } = useTranslation();
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    return localStorage.getItem(STORAGE_KEY) || initial || detectDeviceLang();
  });

  const setLang = (code) => {
    setLangState(code);
    if (i18n && i18n.changeLanguage) {
      i18n.changeLanguage(code);
    }
    // Persist to backend so `preferred_language` in the DB updates.
    // The `/auth/me` PATCH handler triggers `sync_user_roles` when the
    // language actually changes, which reconciles the user's Discord
    // roles to the correct EN/ES access role for their tier.
    //
    // Real errors (500s, network hiccups) get logged so recurring
    // sync failures surface. 401s for unauthenticated visitors are
    // expected and stay silent — they don't have a language to sync.
    api.patch("/auth/me", { preferred_language: code }).catch((err) => {
      const status = err?.response?.status;
      if (status && status !== 401) {
        // eslint-disable-next-line no-console
        console.warn("[language] preferred_language patch failed", status, err?.response?.data);
      }
    });
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, lang);
      document.documentElement.lang = lang;
    }
    if (i18n && i18n.changeLanguage && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  const value = useMemo(() => ({
    lang, setLang, languages: LANGUAGES,
    label: LANGUAGES.find((l) => l.code === lang)?.label || "English",
    flag: LANGUAGES.find((l) => l.code === lang)?.flag || "🇺🇸",
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [lang]);

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
